import {getDatadogRum} from '../../../../util/datadog/datadogRum';
import {BucketedLine} from '../../types';
import {
  GroupedBackendLinesQueryRuns,
  GroupedBucketedQueryRuns,
  GroupedBucketedQueryRunWithGroupedRunConnection,
} from './types';

export function isAggregatedRuns(
  runs:
    | GroupedBucketedQueryRuns
    | GroupedBackendLinesQueryRuns
    | null
    | undefined
): runs is GroupedBucketedQueryRunWithGroupedRunConnection {
  return runs?.__typename === 'GroupedRunConnection';
}

export function profileRenderTimeSaved(
  runsById: Record<string, BucketedLine>,
  type: 'backend' | 'grouped'
) {
  const DD_RUM = getDatadogRum();
  try {
    const numPoints = Object.values(runsById).reduce((acc, run) => {
      const hPoints = (run.historyLines ?? []).reduce((ac, line) => {
        return ac + line.historyLine.length;
      }, 0);
      return acc + hPoints;
    }, 0);
    DD_RUM.addAction('Backend_line_render_saved', {
      numRuns: Object.keys(runsById).length,
      numPoints,
      type,
    });
  } catch (e) {
    DD_RUM.addError(new Error('backend lines profiling error'), {
      reason: 'Error profiling backend lines',
      error: e,
    });
    return;
  }
}

/**
 * Extracts deltas, order, and runFlags from a runs query response.
 * The runFlags property is added client-side by mergeBaselineAndPinnedRuns
 * and doesn't exist in GraphQL types, so we need to access it dynamically.
 */
export function extractDeltasInfo<
  T extends {deltas?: {delta?: any[]; order?: any[]} | null},
>(
  runs: T | null | undefined
): {
  deltas: any[];
  order: any[];
  runFlags?: Record<string, {isBaseline: boolean; isPinned: boolean}>;
} {
  return {
    deltas: runs?.deltas?.delta ?? [],
    order: runs?.deltas?.order ?? [],
    // runFlags is added dynamically by mergeBaselineAndPinnedRuns, not part of GraphQL schema
    runFlags: (runs?.deltas as any)?.runFlags,
  };
}

/**
 * Helper function to update baseline/pinned flags on runs.
 * Handles the case where baseline/pinned selection changes but runs have no data updates.
 * Only clones runsById if flags actually changed.
 */
export function updateRunFlags(
  runsById: Record<string, BucketedLine>,
  runFlags?: Record<string, {isBaseline: boolean; isPinned: boolean}>
): Record<string, BucketedLine> {
  if (!runFlags) {
    return runsById;
  }

  // First pass: check if any flags actually changed
  let hasChanges = false;
  for (const [runId, flags] of Object.entries(runFlags)) {
    const existingRun = runsById[runId];
    if (
      existingRun &&
      (existingRun.isBaseline !== flags.isBaseline ||
        existingRun.isPinned !== flags.isPinned)
    ) {
      hasChanges = true;
      break;
    }
  }

  // If no changes, return original reference
  if (!hasChanges) {
    return runsById;
  }

  // Clone and update only the runs with changed flags
  const updatedRunsById = {...runsById};
  for (const [runId, flags] of Object.entries(runFlags)) {
    const existingRun = updatedRunsById[runId];
    if (
      existingRun &&
      (existingRun.isBaseline !== flags.isBaseline ||
        existingRun.isPinned !== flags.isPinned)
    ) {
      updatedRunsById[runId] = {
        ...existingRun,
        isBaseline: flags.isBaseline,
        isPinned: flags.isPinned,
      };
    }
  }
  return updatedRunsById;
}
